import { useEffect, useRef, useState } from "react";
import "@/App.css";

const SOURCES = [
  { src: "/bg-video.mp4", type: "video/mp4" },
  { src: "/bg-video.webm", type: "video/webm" },
];

const readViewport = () => {
  if (typeof window === "undefined") return { w: 0, h: 0 };
  // Layout viewport: pinch-zoom must magnify the page, not reflow the layout.
  return {
    w: Math.round(window.innerWidth),
    h: Math.round(window.innerHeight),
  };
};

const isLandscapeNow = () => {
  if (typeof window === "undefined") return true;
  if (window.matchMedia)
    return window.matchMedia("(orientation: landscape)").matches;
  return window.innerWidth >= window.innerHeight;
};

export default function App() {
  const videoRef = useRef(null);
  const contentRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [vp, setVp] = useState(readViewport);
  const [landscapeScreen, setLandscapeScreen] = useState(isLandscapeNow);

  useEffect(() => {
    videoRef.current?.play().catch(() => {});
    const update = () => {
      setVp(readViewport());
      setLandscapeScreen(isLandscapeNow());
    };
    update();
    const mq = window.matchMedia?.("(orientation: landscape)");
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    window.screen?.orientation?.addEventListener?.("change", update);
    mq?.addEventListener?.("change", update);
    window.visualViewport?.addEventListener?.("resize", update);
    window.visualViewport?.addEventListener?.("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      window.screen?.orientation?.removeEventListener?.("change", update);
      mq?.removeEventListener?.("change", update);
      window.visualViewport?.removeEventListener?.("resize", update);
      window.visualViewport?.removeEventListener?.("scroll", update);
    };
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || !el.parentElement) return;
    const fit = () => {
      el.classList.add("measuring");
      let lo = 6;
      let hi = 120;
      for (let i = 0; i < 14; i++) {
        const mid = (lo + hi) / 2;
        el.style.fontSize = `${mid}px`;
        if (
          el.scrollHeight <= el.parentElement.clientHeight + 1 &&
          el.scrollWidth <= el.parentElement.clientWidth + 1
        ) {
          lo = mid;
        } else {
          hi = mid;
        }
      }
      el.classList.remove("measuring");
      el.style.fontSize = `${lo}px`;
    };
    fit();
    const raf = requestAnimationFrame(fit);
    return () => cancelAnimationFrame(raf);
  }, [vp.w, vp.h, landscapeScreen, ready]);

  const panelStyle = landscapeScreen
    ? { width: `${Math.round((vp.w * 2) / 3)}px`, height: `${vp.h}px`, left: 0, top: 0 }
    : { width: `${vp.w}px`, height: `${Math.round((vp.h * 2) / 3)}px`, left: 0, top: 0 };

  const textStyle = landscapeScreen
    ? {
        left: `${Math.round((vp.w * 2) / 3)}px`,
        top: 0,
        width: `${vp.w - Math.round((vp.w * 2) / 3)}px`,
        height: `${vp.h}px`,
      }
    : {
        left: 0,
        top: `${Math.round((vp.h * 2) / 3)}px`,
        width: `${vp.w}px`,
        height: `${vp.h - Math.round((vp.h * 2) / 3)}px`,
      };

  return (
    <div className="video-stage" data-testid="video-stage">
      <video
        ref={videoRef}
        data-testid="background-video"
        style={panelStyle}
        className={`background-video ${ready ? "is-ready" : ""}`}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onCanPlay={() => setReady(true)}
        disablePictureInPicture
        controls={false}
      >
        {SOURCES.map((s) => (
          <source key={s.src} src={s.src} type={s.type} />
        ))}
      </video>
      <div
        data-testid="side-text"
        style={textStyle}
        className={`side-text ${ready ? "is-ready" : ""}`}
      >
        <div
          ref={contentRef}
          data-testid="side-text-content"
          className="side-text-content"
        >
          <p className="side-text-lead">
            ..It is a digital twin of one of Alfa Romeo’s most revolutionary
            engines: the Twin Spark, which inspired the name{" "}
            <strong>TWIN SPARX LTD</strong>.
          </p>
          <p>
            <strong>TWIN</strong> refers to the company’s core business:
            Digital Twins.
          </p>
          <p>
            <strong>SPARX</strong> stands for{" "}
            {" "}
            <strong>Sergio Piastra Augmented Reality eXtended</strong>.
          </p>
          <p>
            The name combines the company’s owner, Dr Sergio Piastra, with
            the company’s core areas of expertise:
          </p>
          <ul>
            <li>Digital Twins</li>
            <li>
              Augmented Reality and, more broadly, Extended Reality (XR),
              including Virtual Reality
            </li>
            <li>
              Computer Vision, an AI key enabling technology for Augmented
              Reality
            </li>
          </ul>
          <p className="side-text-cta">
            Bring digital transformation to life with Twin Sparx LTD.
          </p>
          <p className="side-text-email" data-testid="side-text-email">
            info@twinsparx.net
          </p>
        </div>
      </div>
    </div>
  );
}
